import type { Trip } from "@/lib/types";

/**
 * ONE place that decides what a trip's status badge says.
 *
 * Wade, 2026-08-24: *"My TripMuscle New Orleans trip is past date and still
 * shows ACTIVE (please ensure past travel becomes inactive)."* The July 27–29
 * conference was wearing a badge that says the product is still working on it —
 * and so were five other finished trips.
 *
 * The cause is that `active` never meant "still happening". It is the
 * traveller's own watch switch (Active / Paused) and nothing turns it off when
 * the travel is over. The scheduler was never fooled — it has always filtered
 * `departure_date >= CURRENT_DATE` — so nothing was being scraped. Only the
 * badge lied.
 *
 * So: **past WINS over active**, everywhere a badge is drawn. This lives in one
 * exported function precisely because the same badge is rendered on the
 * dashboard card, the trip page and the schedule control, and the last time
 * three surfaces each carried their own copy of a rule, two of them drifted.
 */
export type TripStatusTone = "active" | "muted";

export interface TripStatus {
  label: string;
  tone: TripStatusTone;
  /** Hover/aria explanation. Always says WHY, never just the word. */
  title: string;
  /** True once the travel has happened — the watch switch is moot. */
  past: boolean;
}

export function tripStatus(
  trip: Pick<Trip, "active" | "lifecycle" | "travel_end_date">
): TripStatus {
  // Missing lifecycle = an older backend; assume upcoming rather than retiring
  // a live trip on absent data.
  if (trip.lifecycle === "past") {
    return {
      label: "Past",
      tone: "muted",
      title: trip.travel_end_date
        ? `Travel finished ${trip.travel_end_date}. We stopped watching fares then.`
        : "This travel has happened. We stopped watching fares when it did.",
      past: true,
    };
  }
  return trip.active
    ? { label: "Active", tone: "active", title: "Watching fares for this trip.", past: false }
    : { label: "Paused", tone: "muted", title: "You paused fare watching for this trip.", past: false };
}

/**
 * Split a dashboard list into travel still ahead and travel already done,
 * each keeping the order it arrived in. Past trips are not hidden — they are
 * the traveller's own history and a card that vanishes is the next bug report
 * (§ G35) — they are moved below and dimmed.
 */
export function splitByLifecycle<T extends Pick<Trip, "lifecycle">>(
  trips: T[]
): { upcoming: T[]; past: T[] } {
  const upcoming: T[] = [];
  const past: T[] = [];
  for (const t of trips) (t.lifecycle === "past" ? past : upcoming).push(t);
  return { upcoming, past };
}
